import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { memo, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { boolean, object } from "yup";
import {
  Accordion,
  Box,
  Button,
  Checkbox,
  FlexColBox,
  Form,
  ImageSlider,
  Input,
  Label,
  OverlayScrollbarWrapper,
} from "../../../../common/components";
import { Select } from "../../../../common/components/Select/Select";
import { useForm } from "../../../../common/hooks/useForm";
import { useSelector } from "../../../../common/hooks/useSelector";
import { AppLoadingContext } from "../../../../context";
import ProjectService from "../../../../service/ProjectService";
import { palette, spacing } from "../../../../styles/theme";
import { CreateMockupForm, Image } from "../../../../types";
import { useThemeBreakpoints } from "../../../../utils/themeUtils";
import { DialogTitleWithCloseButton } from "../../../DialogTitleWithCloseButton";

interface Props {
  open: boolean;
  onClose: () => void;
  designs: { designId: string; designName: string; images: Image[] }[];
  projectName: string;
}

const CreateMockupModal = ({ onClose, open, projectName, ...props }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useThemeBreakpoints();
  const userInfo = useSelector((state) => state.userInfo);
  const { setIsAppLoading } = useContext(AppLoadingContext);

  const onCreate = async (values: CreateMockupForm) => {
    setIsAppLoading(true);
    await ProjectService.createMockup(values);
    handleClose();
  };

  const handleClose = () => {
    onClose();
    setIsAppLoading(false);
    formik.resetForm();
  };

  const formik = useForm({
    initialValues: {
      logoType: "jameo",
      marketType: "recreoCanada",
      withIntroPage: true,
      withSummaryPage: true,
      withClosingPage: true,
      onePagePerProduct: true,
      projectTitle: projectName,
      mockupId: "",
      graphicDesigner: `${userInfo.firstName[0]}${userInfo.lastName[0]}`,
      designs: props.designs.map((design) => ({
        ...design,
        proposalNumber: "1.0",
      })),
    },
    onSubmit: onCreate,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object().shape({
      withIntroPage: boolean(),
      withSummaryPage: boolean(),
      onePagePerProduct: boolean(),
      withClosingPage: boolean(),
    }),
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (
        !values.withIntroPage &&
        !values.withSummaryPage &&
        !values.onePagePerProduct &&
        !values.withClosingPage
      ) {
        errors.form = "At least one option must be selected";
      }
      return errors;
    },
  });

  useEffect(() => {
    formik.setFieldValue(
      "designs",
      props.designs.map((design) => ({
        ...design,
        sku: "",
        proposalNumber: "1.0",
      }))
    );
    // eslint-disable-next-line
  }, [props.designs, open]);

  useEffect(() => {
    if (userInfo.firstName && userInfo.lastName) {
      formik.setFieldValue(
        "graphicDesigner",
        `${userInfo.firstName[0]}${userInfo.lastName[0]}`
      );
    }
    // eslint-disable-next-line
  }, [userInfo, open]);

  useEffect(() => {
    if (projectName) {
      formik.setFieldValue("projectTitle", projectName);
    }
    // eslint-disable-next-line
  }, [projectName, open]);

  const designs = formik.values.designs as CreateMockupForm["designs"];

  const handleCheckboxChange =
    (name: keyof CreateMockupForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue(name, e.target.checked);
    };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth={"md"}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          padding: `0 !important`,
        },
      }}
    >
      <OverlayScrollbarWrapper>
        <Box
          sx={{
            padding: {
              xs: `${spacing.wrapperMobile} ${spacing.wrapperMobile} ${spacing.xlMobile}`,
              lg: spacing.wrapperDesktop,
            },
          }}
        >
          <Form formik={formik}>
            <DialogTitleWithCloseButton
              id="customized-dialog-title"
              onClose={handleClose}
            >
              {t("design.createMockup.title")}
            </DialogTitleWithCloseButton>
            <DialogContent>
              <FlexColBox
                gap={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
              >
                <FlexColBox
                  gap={{
                    xs: spacing.groupingMobile,
                    lg: spacing.groupingDesktop,
                  }}
                  maxWidth={"calc(100% - 4px)"} //NOTE - 4px to prevent input from being cut because of box-shadow of 2px each side
                >
                  <Select
                    fullWidth
                    name="marketType"
                    label={t("design.createMockup.marketType.title")}
                    value={formik.values.marketType}
                    options={[
                      {
                        label: t("design.createMockup.marketType.recreoCanada"),
                        value: "recreoCanada",
                      },
                      {
                        label: t("design.createMockup.marketType.recreoUSA"),
                        value: "recreoUSA",
                      },
                      {
                        label: t("design.createMockup.marketType.promo"),
                        value: "promo",
                        disabled: true,
                      },
                    ]}
                  />

                  <FlexColBox gap={spacing.mediumDesktop}>
                    <Label sx={{ mb: 0.5, textAlign: "start" }}>
                      {t("design.createMockup.content")}
                    </Label>

                    <Checkbox
                      name="withIntroPage"
                      checked={formik.values.withIntroPage}
                      onChange={handleCheckboxChange("withIntroPage")}
                      label={t("design.createMockup.withIntroPage")}
                    />
                    <Checkbox
                      name="withSummaryPage"
                      checked={formik.values.withSummaryPage}
                      onChange={handleCheckboxChange("withSummaryPage")}
                      label={t("design.createMockup.withSummaryPage")}
                    />
                    <Checkbox
                      name="onePagePerProduct"
                      checked={formik.values.onePagePerProduct}
                      onChange={handleCheckboxChange("onePagePerProduct")}
                      label={t("design.createMockup.onePagePerProduct")}
                    />
                    <Checkbox
                      name="withClosingPage"
                      checked={formik.values.withClosingPage}
                      onChange={handleCheckboxChange("withClosingPage")}
                      label={t("design.createMockup.withClosingPage")}
                    />
                  </FlexColBox>
                  <Input
                    fullWidth
                    noDefaultValue
                    name={`projectTitle`}
                    value={formik.values.projectTitle}
                    label={t("design.createMockup.projectTitle")}
                  />
                  <Input
                    fullWidth
                    noDefaultValue
                    name={`mockupId`}
                    value={formik.values.mockupId}
                    label={t("design.createMockup.mockupId")}
                  />
                  <Input
                    fullWidth
                    noDefaultValue
                    name={`graphicDesigner`}
                    value={formik.values.graphicDesigner}
                    label={t("design.createMockup.graphicDesigner")}
                  />
                  <Select
                    fullWidth
                    name="logoType"
                    label={t("design.createMockup.logoType.title")}
                    value={formik.values.logoType}
                    options={[
                      {
                        label: t("design.createMockup.logoType.jameo"),
                        value: "jameo",
                      },
                      {
                        label: t("design.createMockup.logoType.initialCustom"),
                        value: "initialCustom",
                      },
                    ]}
                  />
                </FlexColBox>

                {designs.map((d, index) => (
                  <Accordion
                    key={d.designId}
                    summaryText={d.designName}
                    shouldStartExpand
                    accordionSxProps={{
                      borderRadius: "8px",
                      background: palette.grey.lightBackground,
                      border: `1px solid ${palette.grey.lightGrey}`,
                    }}
                  >
                    <Box
                      display={"flex"}
                      gap={spacing.regularDesktop}
                      flexDirection={isMobile ? "column" : "row"}
                    >
                      <Box
                        display={"flex"}
                        minWidth={"25%"}
                        width={"fit-content"}
                      >
                        <ImageSlider
                          images={d.images}
                          containerWidth={isMobile ? "115px" : "188px"}
                          containerHeight={isMobile ? "100px" : "176px"}
                        />
                      </Box>
                      <FlexColBox
                        gap={{
                          xs: spacing.groupingMobile,
                          lg: spacing.groupingDesktop,
                        }}
                        width={"50%"}
                        maxWidth={"calc(100% - 4px)"} //NOTE - 4px to prevent input from being cut because of box-shadow of 2px each side
                      >
                        <Input
                          fullWidth
                          noDefaultValue
                          name={`designs.${index}.proposalNumber`}
                          value={formik.values.designs[index].proposalNumber}
                          label={t("design.createMockup.proposalNumber")}
                        />
                      </FlexColBox>
                    </Box>
                  </Accordion>
                ))}
                {isMobile && (
                  <Button type="submit" fullWidth disabled={!formik.isValid}>
                    <Typography variant="actionBoldBigIcon">
                      {t("general.create")}
                    </Typography>
                  </Button>
                )}
              </FlexColBox>
            </DialogContent>
            {!isMobile && (
              <DialogActions>
                <Button type="submit" disabled={!formik.isValid}>
                  <Typography variant="actionBoldBigIcon">
                    {t("general.create")}
                  </Typography>
                </Button>
              </DialogActions>
            )}
          </Form>
        </Box>
      </OverlayScrollbarWrapper>
    </Dialog>
  );
};

export default memo(CreateMockupModal);
